/**
 * Color utilities for deriving readable UI accents from the per-user color
 * stored in `uzivatele.barva`.
 *
 * The stored values are 8-char RGBA pastels (e.g. "c6f0ff33", "fffabf4d") — a
 * pale base hue at 20–47 % opacity. Rendered as-is they wash out completely
 * against soft fills, so `userDotColor` drops the alpha, keeps the *hue* (so
 * each person stays recognizable), and clamps to a darker, saturated tone that
 * reads on both light and dark themes.
 *
 * Used for the servis author dot (`PlanovaniPage`) and the kontakty salesperson
 * dot (`KontaktyList`).
 */

/**
 * Derive a vivid `#rrggbb` dot color from a stored `uzivatele.barva` value.
 * Returns `undefined` for empty / malformed input so the dot is simply omitted.
 */
export function userDotColor(barva: string | null | undefined): string | undefined {
  const hex = (barva ?? '').trim().replace(/^#/, '').slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const { h, s, l } = rgbToHsl(r, g, b);
  // Boost washed-out pastels to a vivid, darker tone of the same hue.
  const vs = Math.max(s, 0.62);
  const vl = Math.min(l, 0.44);
  return hslToHex(h, vs, vl);
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
